import { getPlayerName as getStoredPlayerName, savePlayerName, sanitizePlayerName } from '../../shared/game-identity.js';
import { createClient } from '@supabase/supabase-js';
import { CONFIG } from '../config.js';
import {
    TIME_CHALLENGE_DURATION_MS,
    TIME_CHALLENGE_SEED_VERSION,
} from './constants.js';
import { dom } from './dom.js';
import { goToLevel, startGame } from './game.js';
import { getMaxErrorForLevel, getPerfectErrorForLevel, updateTotalScoreDisplay } from './scoring.js';
import { state } from './state.js';
import { createSeededRandom } from './utils.js';

const TIME_TIME_ZONE = 'Europe/Zagreb';
const TIME_LEADERBOARD_HISTORY_KEY = 'qdmTimeLeaderboard';
const TIME_LEVEL_OPENING_SEQUENCE = [1, 1, 2, 1, 2, 3, 1, 2, 3, 4];

let supabaseClient = null;
let timerId = null;
let leaderboardHistoryActive = false;
let activeLeaderboardView = 'daily';

function getSupabaseClient() {
    if (!CONFIG.SUPABASE_URL || !CONFIG.SUPABASE_KEY) return null;
    if (!supabaseClient) {
        supabaseClient = createClient(CONFIG.SUPABASE_URL, CONFIG.SUPABASE_KEY);
    }
    return supabaseClient;
}

function getZagrebDateString(date = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: TIME_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
}

function createRunId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.floor(Math.random() * 0xffffffff).toString(16)}`;
}

function getTimeLevelForAnswerIndex(answerIndex) {
    if (answerIndex < TIME_LEVEL_OPENING_SEQUENCE.length) {
        return TIME_LEVEL_OPENING_SEQUENCE[answerIndex];
    }
    return ((answerIndex - TIME_LEVEL_OPENING_SEQUENCE.length) % 5) + 1;
}

function createTimeLeaderboardContext() {
    const challengeDate = getZagrebDateString();
    return {
        challengeId: `qdm-time-${challengeDate}`,
        challengeDate,
        completed: false,
        leaderboardSubmittedAt: null,
    };
}

function createTimeRun(playerName) {
    const challengeDate = getZagrebDateString();
    const now = new Date().toISOString();
    return {
        version: 1,
        seedVersion: TIME_CHALLENGE_SEED_VERSION,
        runId: createRunId(),
        challengeId: `qdm-time-${challengeDate}`,
        challengeDate,
        playerName,
        startedAt: now,
        completedAt: null,
        answerIndex: 0,
        currentLevel: 1,
        answers: [],
        correctCount: 0,
        remainingMs: TIME_CHALLENGE_DURATION_MS,
        activeStartedAt: null,
        completed: false,
        failed: false,
        leaderboardEntryId: null,
        leaderboardSubmittedAt: null,
        leaderboardSubmitError: null,
    };
}

function createTimeScenarioRandom(level) {
    const run = state.timeRun;
    return createSeededRandom(`${run.runId}:v${run.seedVersion}:answer-${run.answerIndex}:level-${level}`);
}

function formatTimer(remainingMs) {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function updateTimerProgress(remainingMs) {
    const ratio = Math.max(0, Math.min(1, remainingMs / TIME_CHALLENGE_DURATION_MS));
    dom.timerProgress.classList.remove('hidden');
    dom.timerProgressFill.style.transform = `scaleX(${ratio})`;
    dom.timerProgressFill.classList.toggle('timer-progress__fill--warning', ratio > 0.1 && ratio <= 0.25);
    dom.timerProgressFill.classList.toggle('timer-progress__fill--danger', ratio <= 0.1);
}

function hideTimerProgress() {
    dom.timerProgress.classList.add('hidden');
    dom.timerProgressFill.style.transform = 'scaleX(1)';
    dom.timerProgressFill.classList.remove(
        'timer-progress__fill--warning',
        'timer-progress__fill--danger',
    );
}

function renderTimer() {
    const run = state.timeRun;
    if (!run) {
        dom.timeStatus.hidden = true;
        hideTimerProgress();
        return;
    }

    dom.timeStatus.hidden = false;
    dom.timeTimer.textContent = formatTimer(run.remainingMs);
    updateTimerProgress(run.remainingMs);
}

function stopTimeTimer() {
    if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
    }
}

function tickTimeTimer() {
    const run = state.timeRun;
    if (state.gameMode !== 'time' || !run || run.completed || run.failed || !run.activeStartedAt) {
        stopTimeTimer();
        return;
    }

    const elapsed = Date.now() - run.activeStartedAt;
    run.remainingMs = Math.max(0, run.remainingMs - elapsed);
    run.activeStartedAt = Date.now();
    renderTimer();

    if (run.remainingMs <= 0) {
        completeTimeRun();
    }
}

function startTimeTimer() {
    const run = state.timeRun;
    if (!run || run.completed || run.failed || run.remainingMs <= 0) return;
    stopTimeTimer();
    run.activeStartedAt = Date.now();
    renderTimer();
    timerId = window.setInterval(tickTimeTimer, 250);
}

export function pauseTimeTimer() {
    const run = state.timeRun;
    if (!run || !run.activeStartedAt) return;

    run.remainingMs = Math.max(0, run.remainingMs - (Date.now() - run.activeStartedAt));
    run.activeStartedAt = null;
    stopTimeTimer();
    renderTimer();
}

function openNameModal() {
    dom.timeNameInput.value = getStoredPlayerName();
    dom.timeNameModal.classList.remove('hidden');
    dom.timeNameInput.focus();
    dom.timeNameInput.select();
}

function closeNameModal() {
    dom.timeNameModal.classList.add('hidden');
}

function isLeaderboardModalOpen() {
    return !dom.timeLeaderboardModal.classList.contains('hidden');
}

function openLeaderboardModal() {
    dom.timeLeaderboardModal.classList.remove('hidden');
    if (!leaderboardHistoryActive) {
        window.history.pushState(
            { ...(window.history.state || {}), [TIME_LEADERBOARD_HISTORY_KEY]: true },
            '',
            window.location.href,
        );
        leaderboardHistoryActive = true;
    }
}

function closeLeaderboardModal() {
    dom.timeLeaderboardModal.classList.add('hidden');
    leaderboardHistoryActive = false;
}

function closeTimeLeaderboard() {
    stopTimeTimer();
    closeLeaderboardModal();
    if (state.gameMode === 'time') {
        dom.timeStatus.hidden = true;
        hideTimerProgress();
        startGame();
    }
}

function handleLeaderboardCloseClick() {
    if (leaderboardHistoryActive) {
        window.history.back();
        return;
    }

    closeTimeLeaderboard();
}

function handleHistoryNavigation() {
    if (!leaderboardHistoryActive || !isLeaderboardModalOpen()) return;
    closeTimeLeaderboard();
}

function setLeaderboardStatus(message, tone = 'neutral') {
    dom.timeSubmitStatus.textContent = message;
    dom.timeSubmitStatus.classList.toggle('text-red-300', tone === 'error');
    dom.timeSubmitStatus.classList.toggle('text-green-300', tone === 'success');
}

function setActiveLeaderboardView(view) {
    activeLeaderboardView = view;
    dom.timeDailyTab.classList.toggle('time-leaderboard-tab--active', view === 'daily');
    dom.timeAllTimeTab.classList.toggle('time-leaderboard-tab--active', view === 'all-time');
}

function formatAttemptTime(createdAt) {
    if (!createdAt) return '';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(createdAt));
}

function renderLeaderboardRows(rows) {
    dom.timeLeaderboardList.innerHTML = '';
    rows.forEach((row) => {
        const item = document.createElement('li');
        item.className = 'daily-leaderboard-row';

        const rank = document.createElement('span');
        rank.className = 'daily-leaderboard-rank';
        rank.textContent = `#${row.leaderboard_rank}`;

        const player = document.createElement('div');
        const name = document.createElement('div');
        name.className = 'daily-leaderboard-name';
        name.textContent = row.player_name;
        const meta = document.createElement('div');
        meta.className = 'daily-leaderboard-meta';
        const attemptTime = formatAttemptTime(row.created_at);
        meta.textContent = `${row.completed_levels} answered${attemptTime ? ` - ${attemptTime}` : ''}`;
        player.append(name, meta);

        const score = document.createElement('span');
        score.className = 'daily-leaderboard-score';
        score.textContent = row.score;

        item.append(rank, player, score);
        dom.timeLeaderboardList.appendChild(item);
    });
}

async function fetchTimeLeaderboard(run, view = activeLeaderboardView) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase is not configured.');

    const tableName = view === 'daily'
        ? 'qdm_time_daily_leaderboard'
        : 'qdm_time_all_time_leaderboard';

    let query = client
        .from(tableName)
        .select('leaderboard_rank, player_name, score, duration_ms, completed_levels, created_at, challenge_date')
        .order('leaderboard_rank', { ascending: true })
        .limit(20);

    if (view === 'daily') {
        query = query.eq('challenge_date', run.challengeDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
}

async function submitTimeRunIfNeeded(run) {
    if (!run.completed || run.leaderboardSubmittedAt) return run;

    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase is not configured.');

    const payload = {
        challenge_type: 'time',
        challenge_id: run.challengeId,
        challenge_date: run.challengeDate,
        seed_version: run.seedVersion,
        player_name: run.playerName,
        player_fingerprint: null,
        score: run.correctCount,
        duration_ms: TIME_CHALLENGE_DURATION_MS,
        completed_levels: run.answers.length,
        level_scores: run.answers.map((answer) => answer.score),
        client_version: 'qdm-time-v1',
    };

    const { data, error } = await client
        .from('qdm_leaderboard_entries')
        .insert(payload)
        .select('id, created_at')
        .single();

    if (error) {
        run.leaderboardSubmitError = error.message;
        throw error;
    }

    run.leaderboardEntryId = data?.id || null;
    run.leaderboardSubmittedAt = data?.created_at || new Date().toISOString();
    run.leaderboardSubmitError = null;
    return run;
}

async function showTimeLeaderboard(run = state.timeRun, view = activeLeaderboardView) {
    if (!run) run = createTimeLeaderboardContext();

    setActiveLeaderboardView(view);
    dom.timeLeaderboardDate.textContent = view === 'daily' ? run.challengeDate : 'All time';
    dom.timeLeaderboardList.innerHTML = '';
    openLeaderboardModal();

    try {
        if (run.completed && !run.leaderboardSubmittedAt) {
            setLeaderboardStatus('Submitting score...');
            run = await submitTimeRunIfNeeded(run);
            state.timeRun = run;
        }

        const rows = await fetchTimeLeaderboard(run, view);
        setLeaderboardStatus(run.completed ? 'Score submitted.' : 'Time scores.', run.completed ? 'success' : 'neutral');
        renderLeaderboardRows(rows);
    } catch (error) {
        console.error('Time leaderboard error:', error);
        setLeaderboardStatus('Leaderboard unavailable.', 'error');
    }
}

export async function completeTimeRun() {
    const run = state.timeRun;
    if (!run || run.completed || run.failed) return;

    pauseTimeTimer();
    run.completed = true;
    run.remainingMs = 0;
    run.completedAt = new Date().toISOString();
    state.timeRun = run;
    updateTotalScoreDisplay();
    renderTimer();

    dom.submitBtn.classList.add('hidden');
    dom.replayLevelBtn.classList.add('hidden');
    dom.slider.disabled = true;
    dom.slider.classList.add('submitted');
    dom.resultTextEl.innerHTML = `<span class="text-green-400">Time! ${run.correctCount} correct.</span>`;

    await showTimeLeaderboard(run, activeLeaderboardView);
}

function startTimeRun(run) {
    stopTimeTimer();
    run.currentLevel = getTimeLevelForAnswerIndex(run.answerIndex);
    state.timeRun = run;
    startGame({
        mode: 'time',
        timeRun: run,
        currentProblem: run.currentLevel,
        createScenarioRandom: createTimeScenarioRandom,
    });
    updateTotalScoreDisplay();
    startTimeTimer();
}

function startNewTimeRun(playerName) {
    savePlayerName(playerName);
    closeLeaderboardModal();
    closeNameModal();
    startTimeRun(createTimeRun(playerName));
}

function handleTimeButtonClick() {
    showTimeLeaderboard(state.timeRun || createTimeLeaderboardContext(), activeLeaderboardView);
}

function handleTimePlayClick() {
    const playerName = sanitizePlayerName(getStoredPlayerName());
    if (!playerName) {
        openNameModal();
        return;
    }

    startNewTimeRun(playerName);
}

function handleNameSubmit(event) {
    event.preventDefault();
    const playerName = sanitizePlayerName(dom.timeNameInput.value);
    if (!playerName) {
        dom.timeNameInput.focus();
        return;
    }

    closeNameModal();
    startNewTimeRun(playerName);
}

export function recordTimeAnswer({ level, rawValue, yourError }) {
    const run = state.timeRun;
    if (!run || run.completed || run.failed) return null;

    const perfectError = getPerfectErrorForLevel(level);
    const maxError = getMaxErrorForLevel(level);
    const isPerfect = yourError <= perfectError;
    const isSafe = yourError < maxError;

    if (!isSafe) {
        run.failed = true;
        run.activeStartedAt = null;
        stopTimeTimer();
        dom.resultTextEl.innerHTML = `<span class="text-red-300">Outside range. Run failed.</span>`;
        return { outcome: 'failed', score: 0 };
    }

    const score = isPerfect ? 1 : 0;
    if (score === 1) {
        run.correctCount += 1;
    }

    run.answers.push({
        level,
        rawValue,
        errorNm: Number(yourError.toFixed(2)),
        score,
        submittedAt: new Date().toISOString(),
    });
    run.answerIndex = run.answers.length;
    run.currentLevel = getTimeLevelForAnswerIndex(run.answerIndex);
    updateTotalScoreDisplay();

    dom.resultTextEl.innerHTML = isPerfect
        ? `<span class="text-green-400">Perfect: +1 correct</span>`
        : `<span class="text-yellow-300">Within range: +0</span>`;

    return { outcome: isPerfect ? 'perfect' : 'safe', score };
}

export function handleTimeActionButton() {
    const run = state.timeRun;
    if (!run) return;

    if (run.failed) {
        startNewTimeRun(run.playerName);
        return;
    }

    if (run.completed) {
        showTimeLeaderboard(run, activeLeaderboardView);
        return;
    }

    goToLevel(run.currentLevel);
    startTimeTimer();
}

function handleLeaderboardTabClick(view) {
    showTimeLeaderboard(state.timeRun || createTimeLeaderboardContext(), view);
}

export function initTimeChallenge() {
    dom.timeChallengeBtn.addEventListener('click', handleTimeButtonClick);
    dom.timeNameForm.addEventListener('submit', handleNameSubmit);
    dom.timeNameCancelBtn.addEventListener('click', closeNameModal);
    dom.timeLeaderboardCloseBtn.addEventListener('click', handleLeaderboardCloseClick);
    dom.timeLeaderboardPlayBtn.addEventListener('click', handleTimePlayClick);
    dom.timeDailyTab.addEventListener('click', () => handleLeaderboardTabClick('daily'));
    dom.timeAllTimeTab.addEventListener('click', () => handleLeaderboardTabClick('all-time'));
    window.addEventListener('popstate', handleHistoryNavigation);
}
